/**
 * EIS-1124 PART A -- shared data hook for the rep-facing "Possible
 * duplicates" panel (Details) and its Summary chip.
 *
 * Both call sites use this hook, and it keeps a MODULE-LEVEL cache keyed by
 * practitioner id so the two mounted together issue exactly ONE
 * GET /api/matching/for_practitioner (see PossibleDuplicates.test.tsx's
 * "shared module-level cache" describe block) -- component state alone can't
 * do that, since Summary and the Details panel are separate component
 * subtrees with no shared parent state.
 *
 * PART B (turtle_api, a separate loop) implements this route; there is no
 * live backend yet, so this hook is proven only against jest-mocked
 * getData/postData (see the CONTRACT in ./types).
 */
import { useCallback, useEffect, useState } from 'react';
import useExternalApiService from 'helpers/ExternalApiService';
import { ForPractitionerResponse, ForPractitionerData, Suggestion } from './types';

interface FetchState {
  data: ForPractitionerData | null;
  loading: boolean;
  error: string | null;
}

interface CacheEntry {
  state: FetchState;
  // Set synchronously (before any await) while a fetch is outstanding, so a
  // second hook instance mounting in the same tick sees it and skips its own
  // fetch, even though the first fetch's network round-trip hasn't resolved.
  promise: Promise<void> | null;
  // EIS-1124 part A round 3, finding 3: bumped every time a new GET starts
  // (mount fetch OR refetch). A fetch closes over the generation it was
  // started at; when its response comes back, it may only write
  // data/error -- and only clear `promise` -- if `entry.generation` still
  // equals the generation it captured, i.e. no NEWER fetch has since
  // started. This makes a `refetch()` while one is already in flight
  // SUPERSEDE it: the older request's late response is dropped rather than
  // clobbering fresher data, and its `finally` can't null out the marker a
  // newer, still-outstanding request owns.
  generation: number;
  listeners: Set<() => void>;
}

const EMPTY_STATE: FetchState = { data: null, loading: false, error: null };

const cache = new Map<number, CacheEntry>();

const getEntry = (practitionerId: number): CacheEntry => {
  let entry = cache.get(practitionerId);
  if (!entry) {
    entry = { state: { ...EMPTY_STATE }, promise: null, generation: 0, listeners: new Set() };
    cache.set(practitionerId, entry);
  }
  return entry;
};

const setEntryState = (entry: CacheEntry, patch: Partial<FetchState>) => {
  entry.state = { ...entry.state, ...patch };
  entry.listeners.forEach((listener) => listener());
};

type GetData = (endpoint: string) => Promise<ForPractitionerResponse | undefined>;

const fetchForPractitioner = (getData: GetData, practitionerId: number, entry: CacheEntry): Promise<void> => {
  const generation = ++entry.generation;
  setEntryState(entry, { loading: true, error: null });
  const promise = (async () => {
    try {
      const response = await getData(`/api/matching/for_practitioner?practitioner_id=${practitionerId}`);
      // A newer fetch (another refetch()) started while this one was in
      // flight -- its response, whenever it lands, is the one allowed to
      // write state. Drop this stale response rather than clobber it.
      if (entry.generation !== generation) return;
      // The backend wrapper answers HTTP 200 with success:false on handler
      // errors, so fetch won't throw -- check the flag (MatchApproval.tsx's
      // fetchGroups does the same).
      if (!response || response.success === false) {
        setEntryState(entry, {
          loading: false,
          error: (response && response.error_msg) || 'Failed to load possible duplicates.',
        });
      } else {
        const { active_group, suggestions, my_pending } = response;
        setEntryState(entry, { loading: false, error: null, data: { active_group, suggestions, my_pending } });
      }
    } catch (err) {
      if (entry.generation !== generation) return; // superseded; see above
      console.error('Error fetching possible duplicates:', err);
      setEntryState(entry, { loading: false, error: 'Failed to load possible duplicates.' });
    } finally {
      // Only clear the shared marker if this fetch's generation is still
      // the current one -- a stale request's finally must never null out a
      // newer, still-outstanding request's promise. Comparing the
      // generation token (checker LOW nit, round 3) rather than promise
      // identity makes this independent of statement order between
      // creating the IIFE and assigning it to `entry.promise` below.
      if (entry.generation === generation) {
        entry.promise = null;
      }
    }
  })();
  entry.promise = promise;
  return promise;
};

export interface UsePossibleDuplicatesResult extends FetchState {
  refetch: () => Promise<void>;
  propose: (suggestion: Suggestion) => Promise<any>;
  dismiss: (suggestion: Suggestion) => Promise<any>;
}

/**
 * @param practitionerId the current baseball card's practitioner id
 * @param enabled gate: no fetch, and cached/returned data is always the
 *   empty state, unless true. Callers pass
 *   `Boolean(store.user?.earlyRelease) && store.currentPractitioner?.id != null`.
 */
const usePossibleDuplicates = (
  practitionerId: number | undefined,
  enabled: boolean,
): UsePossibleDuplicatesResult => {
  const apiUrl = process.env.REACT_APP_EAI_URL;
  const { getData, postData } = useExternalApiService(apiUrl);
  const [, forceRender] = useState(0);

  useEffect(() => {
    if (!enabled || !practitionerId) return undefined;
    const entry = getEntry(practitionerId);
    const listener = () => forceRender((n) => n + 1);
    entry.listeners.add(listener);
    if (!entry.promise && entry.state.data === null && !entry.state.error) {
      fetchForPractitioner(getData, practitionerId, entry);
    }
    return () => {
      entry.listeners.delete(listener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, practitionerId]);

  const refetch = useCallback(async () => {
    if (!enabled || !practitionerId) return;
    const entry = getEntry(practitionerId);
    // Always start a fresh, generation-tagged fetch -- see finding 3 above.
    // If one is already in flight this SUPERSEDES it rather than reusing or
    // racing it: the in-flight one's eventual response is discarded by the
    // generation check in fetchForPractitioner, so this call's result is
    // always the one that wins.
    await fetchForPractitioner(getData, practitionerId, entry);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, practitionerId]);

  const propose = useCallback(
    async (suggestion: Suggestion) => {
      const response = await postData('/api/matching/propose', {
        practitioner_ids: (suggestion.members || []).map((m) => m.id),
        master_practitioner_id: suggestion.master_practitioner_id,
        reason: 'Rep confirmed on card',
        from_suggestion_id: suggestion.id,
      });
      await refetch();
      return response;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refetch],
  );

  const dismiss = useCallback(
    async (suggestion: Suggestion) => {
      const response = await postData('/api/matching/dismiss', {
        group_id: suggestion.id,
        reason: 'rep_no',
      });
      await refetch();
      return response;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refetch],
  );

  const state = enabled && practitionerId ? getEntry(practitionerId).state : EMPTY_STATE;

  return { ...state, refetch, propose, dismiss };
};

export default usePossibleDuplicates;

// Test-only escape hatch: the cache is module-level and otherwise outlives
// individual `it()` blocks within one test file.
export const __resetPossibleDuplicatesCacheForTests = () => cache.clear();
